import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../../api/ad.api';
import { AdPosition } from '../../../types/Ad.api.type';

const POSITIONS_WITH_ADGROUPID: AdPosition[] = [
  'HOME_TOP',
  'HOME_MIDDLE',
  'MAIN_FEED',
  'MAIN_FEED_TOP',
  'ATTENDANCE',
  'DETAIL_COMMENT',
  'BENEFIT',
  'ATTENDANCE_RESULT',
  'EXCHANGE_RESULT',
  'WEEKLY_MISSION_RESULT',
];

const POSITION_LABELS: Record<AdPosition, string> = {
  HOME_TOP: '홈 상단',
  HOME_MIDDLE: '홈 중단',
  MAIN_FEED_TOP: '메인 상단',
  MAIN_FEED: '메인 피드',
  MAIN_FEED_IMAGE: '메인 피드 이미지',
  POPULAR_FEED_TOP: '인기설문 상단',
  POPULAR_FEED: '인기설문',
  MY_FEED_TOP: '내설문 상단',
  MY_FEED: '내설문',
  DETAIL_COMMENT: '상세 댓글',
  ATTENDANCE: '출석 달력 하단',
  BENEFIT: '혜택 페이지',
  ATTENDANCE_RESULT: '출석 완료',
  EXCHANGE_RESULT: '환전 완료',
  WEEKLY_MISSION_RESULT: '미션 완료',
};

const DAYS_OPTIONS = [7, 14, 30, 90];

/** G1-2 섹션 4: 광고ID별 일별/누적 집계 */
const AdIdAggregationTable = () => {
  const [position, setPosition] = useState<AdPosition>('HOME_TOP');
  const [days, setDays] = useState(14);

  const { data, isLoading } = useQuery({
    queryKey: ['idAggregation', position, days],
    queryFn: () => adApi.getIdAggregation(position, days).then((res) => res.data),
    staleTime: 1000 * 60 * 60,
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2">
        <h4 className="text-sm font-semibold text-gray-700">4. 광고ID별 일별 집계</h4>
        <div className="flex gap-2">
          <select
            value={position}
            onChange={(e) => setPosition(e.target.value as AdPosition)}
            className="text-xs border rounded px-2 py-1"
          >
            {POSITIONS_WITH_ADGROUPID.map((p) => (
              <option key={p} value={p}>
                {POSITION_LABELS[p]}
              </option>
            ))}
          </select>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-xs border rounded px-2 py-1"
          >
            {DAYS_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d}일
              </option>
            ))}
          </select>
        </div>
      </div>

      {data?.tossAdGroupId && (
        <div className="text-[10px] text-gray-500 mb-1 font-mono">광고ID: {data.tossAdGroupId}</div>
      )}

      {isLoading ? (
        <div className="text-xs text-gray-400">로딩 중...</div>
      ) : !data || data.daily.length === 0 ? (
        <div className="text-xs text-gray-400 italic">데이터가 없습니다.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="border px-2 py-1 text-left">일자</th>
                <th className="border px-2 py-1 text-right">토스 노출</th>
                <th className="border px-2 py-1 text-right">자체 노출</th>
                <th className="border px-2 py-1 text-right">한도 도달 유저</th>
              </tr>
            </thead>
            <tbody>
              {data.daily.map((row) => (
                <tr key={row.date ?? 'cumulative'} className="hover:bg-gray-50">
                  <td className="border px-2 py-1 font-mono">{row.date}</td>
                  <td className="border px-2 py-1 text-right">{row.tossImpressions.toLocaleString()}</td>
                  <td className="border px-2 py-1 text-right">{row.selfImpressions.toLocaleString()}</td>
                  <td className="border px-2 py-1 text-right">{row.limitReachedUsers.toLocaleString()}명</td>
                </tr>
              ))}
              <tr className="bg-gray-100 font-semibold">
                <td className="border px-2 py-1">누적 ({days}일)</td>
                <td className="border px-2 py-1 text-right">{data.cumulative.tossImpressions.toLocaleString()}</td>
                <td className="border px-2 py-1 text-right">{data.cumulative.selfImpressions.toLocaleString()}</td>
                <td className="border px-2 py-1 text-right">{data.cumulative.limitReachedUsers.toLocaleString()}명</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdIdAggregationTable;
